import {
  type AccountFlowSummaries,
  type AccountFlowSummariesQuery,
  type CashFlowQuery,
  type CashFlowReport,
  type ExpenseBreakdown,
  type ExpenseBreakdownQuery,
  type ExpenseGroupBy,
  type LargestTransaction,
  type LargestTransactionsQuery,
  reportingKeys,
} from '@taler/api-client'
import { type CurrencyCode, formatLocalDate } from '@taler/domain'
import { useQuery } from '@tanstack/vue-query'
import { computed, type ComputedRef } from 'vue'
import { useApi } from '@/app/api'
import { retrying } from '@/app/retrying'
import type { ReportRange } from '../internal/report-range.ts'

export interface ReportFlowsOptions {
  readonly ledgerId: () => string | undefined
  readonly range: () => ReportRange
  readonly displayCurrency: () => CurrencyCode
  readonly enabled: () => boolean
}

export interface FlowResult<T> {
  readonly data: ComputedRef<T | undefined>
  readonly isLoading: ComputedRef<boolean>
  readonly error: ComputedRef<Error | null>
  readonly retrying: ComputedRef<boolean>
  retry(): void
}

function windowOf(options: ReportFlowsOptions) {
  const range = options.range()
  return {
    from: formatLocalDate(range.from),
    until: formatLocalDate(range.until),
    displayCurrency: options.displayCurrency() as CashFlowQuery['displayCurrency'],
  }
}

export function useCashFlowQuery(options: ReportFlowsOptions): FlowResult<CashFlowReport> {
  const api = useApi()
  const active = computed(() => options.enabled() && options.ledgerId() !== undefined)
  const query = computed<CashFlowQuery>(() => ({
    ...windowOf(options),
    bucket: options.range().bucket,
  }))
  const flow = useQuery({
    queryKey: computed(() => reportingKeys.cashFlow(options.ledgerId() ?? '', query.value)),
    queryFn: ({ signal }) => api.reporting.cashFlow(options.ledgerId()!, query.value, signal),
    enabled: active,
    retry: false,
  })
  return resultOf(flow, active)
}

export function useExpenseBreakdownQuery(
  options: ReportFlowsOptions & { readonly groupBy: () => ExpenseGroupBy },
): FlowResult<ExpenseBreakdown> {
  const api = useApi()
  const active = computed(() => options.enabled() && options.ledgerId() !== undefined)
  const query = computed<ExpenseBreakdownQuery>(() => ({
    ...windowOf(options),
    bucket: options.range().bucket,
    groupBy: options.groupBy(),
  }))
  const flow = useQuery({
    queryKey: computed(() => reportingKeys.expenseBreakdown(options.ledgerId() ?? '', query.value)),
    queryFn: ({ signal }) => api.reporting.expenseBreakdown(options.ledgerId()!, query.value, signal),
    enabled: active,
    retry: false,
  })
  return resultOf(flow, active)
}

export function useLargestTransactionsQuery(
  options: ReportFlowsOptions & { readonly limit: number },
): FlowResult<readonly LargestTransaction[]> {
  const api = useApi()
  const active = computed(() => options.enabled() && options.ledgerId() !== undefined)
  const query = computed<LargestTransactionsQuery>(() => ({
    ...windowOf(options),
    limit: options.limit,
  }))
  const flow = useQuery({
    queryKey: computed(() => reportingKeys.largestTransactions(options.ledgerId() ?? '', query.value)),
    queryFn: ({ signal }) => api.reporting.largestTransactions(options.ledgerId()!, query.value, signal),
    enabled: active,
    retry: false,
  })
  return resultOf(flow, active)
}

export function useAccountFlowSummariesQuery(options: ReportFlowsOptions): FlowResult<AccountFlowSummaries> {
  const api = useApi()
  const active = computed(() => options.enabled() && options.ledgerId() !== undefined)
  const query = computed<AccountFlowSummariesQuery>(() => windowOf(options))
  const flow = useQuery({
    queryKey: computed(() => reportingKeys.accountFlowSummaries(options.ledgerId() ?? '', query.value)),
    queryFn: ({ signal }) => api.reporting.accountFlowSummaries(options.ledgerId()!, query.value, signal),
    enabled: active,
    retry: false,
  })
  return resultOf(flow, active)
}

function resultOf<T>(
  flow: {
    data: { value: T | undefined }
    isPending: { value: boolean }
    isError: { value: boolean }
    isFetching: { value: boolean }
    error: { value: Error | null }
    refetch(): unknown
  },
  active: ComputedRef<boolean>,
): FlowResult<T> {
  return {
    data: computed(() => flow.data.value),
    isLoading: computed(() => active.value && flow.isPending.value),
    error: computed(() => flow.error.value),
    retrying: retrying(flow),
    retry: () => {
      void flow.refetch()
    },
  }
}
